import { Controller } from 'stimulus'
import { debounce } from 'throttle-debounce'
import { loader } from '~/common/helpers/google_maps_loader'
import './member_directory_map_component.css'

let Map, AdvancedMarkerElement
async function importLibraries() {
  if (Map && AdvancedMarkerElement) return

  const [maps, marker] = await Promise.all([
    loader.importLibrary('maps'),
    loader.importLibrary('marker'),
  ])

  Map = maps.Map
  AdvancedMarkerElement = marker.AdvancedMarkerElement
}

// Apparently, we can't cleanup Google maps instances
// So we gotta reuse them by id!
let mapsById = {}

export default class extends Controller {
  static values = {
    userLocation: Array,
    mapDataUrl: String,
    mapId: String,
  }

  static targets = ['map', 'loading', 'inaccuracyNotice']

  connect() {
    this.initializeMap()
    this.updateMarkers()

    if (!localStorage.hideInaccuracyNotice) {
      this.inaccuracyNoticeTarget.classList.remove('hidden')
    }
  }

  hideInaccuracyNotice() {
    localStorage.hideInaccuracyNotice = true
    this.inaccuracyNoticeTarget.classList.add('hidden')
  }

  async initializeMap() {
    if (mapsById[this.mapIdValue]) {
      this.map = mapsById[this.mapIdValue]
      return
    }

    await importLibraries()

    // Default to world view, will center on user location if available
    const defaultCenter = { lat: 0, lng: 0 }
    const defaultZoom = 2

    // Use user location if provided
    const center =
      this.userLocationValue?.length === 2
        ? { lat: this.userLocationValue[0], lng: this.userLocationValue[1] }
        : defaultCenter

    const zoom = this.userLocationValue?.length === 2 ? 7 : defaultZoom

    this.map = new Map(this.mapTarget, {
      center,
      zoom,
      mapTypeControl: false,
      fullscreenControl: true,
      streetViewControl: false,
      rotateControl: false,
      mapId: this.mapIdValue,
    })
    this.map.addListener('bounds_changed', this.debouncedUpdateMarkers)
    mapsById[this.mapIdValue] = this.map
  }

  debouncedUpdateMarkers = debounce(300, () => this.updateMarkers())

  async updateMarkers() {
    if (!this.mapDataUrlValue) return

    const bounds = this.map.getBounds()
    if (!bounds) return

    this.showLoading()

    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()
    const zoom = this.map.getZoom()

    const params = new URLSearchParams({
      'bounds[north]': ne.lat(),
      'bounds[south]': sw.lat(),
      'bounds[east]': ne.lng(),
      'bounds[west]': sw.lng(),
      zoom_level: zoom,
    })

    try {
      const request = fetch(`${this.mapDataUrlValue}?${params}`)
      this.lastRequest = request
      const response = await request
      const data = await response.json()

      if (this.lastRequest != request) return

      this.resetMarkers()
      this.renderMarkers(data)
    } catch (error) {
      console.error('Failed to load members:', error)
    }

    this.hideLoading()
  }

  resetMarkers() {
    if (this.markers) {
      this.markers.forEach((marker) => (marker.map = null))
    }
    this.markers = []
  }

  renderMarkers(data) {
    if (!data) return
    data.forEach((item) => {
      let marker
      if (item.type == 'member') {
        marker = this.createMemberMarker(item)
      } else {
        marker = this.createClusterMarker(item)
      }
      this.markers.push(marker)
    })
  }

  createClusterMarker(cluster) {
    return new AdvancedMarkerElement({
      position: { lat: cluster.lat, lng: cluster.lng },
      content: this.htmlToElement(cluster.html),
      map: this.map,
    })
  }

  createMemberMarker(member) {
    const marker = new AdvancedMarkerElement({
      position: { lat: member.lat, lng: member.lng },
      content: this.htmlToElement(member.html),
      map: this.map,
    })

    marker.addListener('click', () => {
      window.open(`${location.origin}/members/${member.id}`, '_blank')
    })

    return marker
  }

  showLoading() {
    this.loadingTarget.style.display = 'flex'
  }

  hideLoading() {
    this.loadingTarget.style.display = 'none'
  }

  htmlToElement(html) {
    const template = document.createElement('template')
    template.innerHTML = html
    return template.content.firstElementChild
  }
}
